import { useId, useState } from "react";

import {
  errorMessage,
  useEvaluations,
  useRecordEvaluation,
  useRecordTarget,
  useTargets,
} from "../../api/queries";
import type { ConsoleConfig, Dataset } from "../../api/types";
import {
  formatDateTime,
  formatScore,
  NOT_A_NUMBER,
  parseInteger,
  parseNumber,
  UNKNOWN,
} from "../../lib/format";
import { metricLabel, metricsFor } from "../../lib/metrics";
import { Dialog } from "../ui/Dialog";
import { Field } from "../ui/Field";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "../ui/StateBlock";

interface RecordBarDialogProps {
  dataset: Dataset;
  config: ConsoleConfig | undefined;
  onClose: () => void;
}

/**
 * Record what this dataset has to beat.
 *
 * The metric comes from the published table for this dataset's task type, and
 * the direction is never asked for: it is a property of the metric, and a bar
 * whose direction was typed by the person who wanted to clear it is how a deal
 * gets claimed on a number that never existed.
 *
 * Args:
 *     dataset: The dataset the bar belongs to.
 *     config: The published configuration, for the metric table.
 *     onClose: Close the dialog.
 *
 * Returns:
 *     The dialog.
 */
function RecordBarDialog({
  dataset,
  config,
  onClose,
}: RecordBarDialogProps): JSX.Element {
  const metricId = useId();
  const thresholdId = useId();
  const baselineId = useId();
  const reasonId = useId();
  const evidenceId = useId();

  const options = metricsFor(config, dataset.task_type);
  const [metric, setMetric] = useState(options[0]?.metric ?? "");
  const [threshold, setThreshold] = useState("");
  const [baselineKnown, setBaselineKnown] = useState(false);
  const [baseline, setBaseline] = useState("");
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState("");
  const record = useRecordTarget(dataset.id);

  // Parsed once, so the button and the request agree: a comma decimal is a
  // number, and anything that is not one never becomes a JSON null nobody sent.
  const thresholdValue = parseNumber(threshold);
  const baselineValue = parseNumber(baseline);
  const ready =
    metric !== "" &&
    thresholdValue !== null &&
    evidence.trim() !== "" &&
    (baselineKnown ? baselineValue !== null : reason.trim() !== "");

  return (
    <Dialog
      title="What are we trying to beat?"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={record.isPending || !ready}
            onClick={() =>
              record.mutate(
                {
                  metric,
                  threshold_value: thresholdValue ?? 0,
                  baseline_value: baselineKnown ? baselineValue : null,
                  baseline_absent_reason: baselineKnown ? null : reason.trim(),
                  evidence: evidence.trim(),
                },
                { onSuccess: onClose },
              )
            }
          >
            {record.isPending ? "Recording…" : "Record the bar"}
          </button>
        </>
      }
    >
      {options.length === 0 ? (
        <p className="dialog__warning">
          The console has not published which measures apply to this kind of
          problem, so there is nothing to pick from yet. This screen does not
          make one up.
        </p>
      ) : (
        <Field
          label="Which measure?"
          htmlFor={metricId}
          hint="Only the ones that belong to this kind of problem are offered — the console refuses the others by name."
        >
          <select
            id={metricId}
            className="select"
            value={metric}
            onChange={(event) => setMetric(event.target.value)}
          >
            {options.map((option) => (
              <option key={option.metric} value={option.metric}>
                {option.label}
                {option.direction === "higher_is_better"
                  ? " — higher is better"
                  : " — lower is better"}
              </option>
            ))}
          </select>
        </Field>
      )}

      <Field
        label="The bar the client set"
        htmlFor={thresholdId}
        hint="What we agreed to beat. A bar you do not have yet is no bar, which is a state — leave this and come back."
        error={
          threshold.trim() !== "" && thresholdValue === null
            ? NOT_A_NUMBER
            : undefined
        }
      >
        <input
          id={thresholdId}
          className="input input--narrow mono"
          value={threshold}
          inputMode="decimal"
          onChange={(event) => setThreshold(event.target.value)}
        />
      </Field>

      <label className="check">
        <input
          type="checkbox"
          checked={baselineKnown}
          onChange={(event) => setBaselineKnown(event.target.checked)}
        />
        <span>We know what their own model scores</span>
      </label>

      {baselineKnown ? (
        <Field
          label="Their own model's score"
          htmlFor={baselineId}
          hint="A different number from the bar. Plenty of clients set the bar above their own."
          error={
            baseline.trim() !== "" && baselineValue === null
              ? NOT_A_NUMBER
              : undefined
          }
        >
          <input
            id={baselineId}
            className="input input--narrow mono"
            value={baseline}
            inputMode="decimal"
            onChange={(event) => setBaseline(event.target.value)}
          />
        </Field>
      ) : (
        <Field
          label="Why there is no baseline"
          htmlFor={reasonId}
          hint="An invented baseline is the worst number in the building, so the console asks for the reason instead."
        >
          <input
            id={reasonId}
            className="input"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="They have no model in production for this"
          />
        </Field>
      )}

      <Field
        label="Where did the bar come from?"
        htmlFor={evidenceId}
        hint="A call, a mail, a slide with a date on it."
        error={record.isError ? errorMessage(record.error) : undefined}
      >
        <input
          id={evidenceId}
          className="input"
          value={evidence}
          onChange={(event) => setEvidence(event.target.value)}
          placeholder="Kick-off call with C. Meyer, 30 July 2026"
        />
      </Field>
    </Dialog>
  );
}

interface RecordEvaluationDialogProps {
  dataset: Dataset;
  config: ConsoleConfig | undefined;
  onClose: () => void;
}

/**
 * Record a result somebody measured somewhere else.
 *
 * The console did not run it, and the record says so: `reported`, with the
 * link to the run it came from, which is required. Whether the bar was cleared
 * is the one thing the console computes, and it is never something anybody
 * types.
 *
 * Args:
 *     dataset: The dataset the result belongs to.
 *     config: The published configuration, for the metric table.
 *     onClose: Close the dialog.
 *
 * Returns:
 *     The dialog.
 */
function RecordEvaluationDialog({
  dataset,
  config,
  onClose,
}: RecordEvaluationDialogProps): JSX.Element {
  const versionId = useId();
  const metricId = useId();
  const valueId = useId();
  const runId = useId();
  const notesId = useId();

  const options = metricsFor(config, dataset.task_type);
  const [versionNumber, setVersionNumber] = useState(
    String(dataset.versions.latest?.number ?? 1),
  );
  const [metric, setMetric] = useState(
    dataset.gate.metric ?? options[0]?.metric ?? "",
  );
  const [value, setValue] = useState("");
  const [runUrl, setRunUrl] = useState("");
  const [notes, setNotes] = useState("");
  const record = useRecordEvaluation(dataset.id);

  const measured = parseNumber(value);
  const version = parseInteger(versionNumber);
  const ready =
    metric !== "" && measured !== null && runUrl.trim() !== "" && version !== null;

  return (
    <Dialog
      title="Record a result"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={record.isPending || !ready}
            onClick={() =>
              record.mutate(
                {
                  version_number: version ?? 0,
                  metric,
                  value: measured ?? 0,
                  run_url: runUrl.trim(),
                  notes: notes.trim() ? notes.trim() : null,
                },
                { onSuccess: onClose },
              )
            }
          >
            {record.isPending ? "Recording…" : "Record it"}
          </button>
        </>
      }
    >
      <p className="dialog__text">
        The console did not run this and will not say it did: the result is
        labelled <em>reported</em>, beside the run it came from. What it works
        out for itself is whether the bar was cleared.
      </p>

      <Field
        label="Which version?"
        htmlFor={versionId}
        error={
          versionNumber.trim() !== "" && version === null
            ? "That is not a version number — write it as 3."
            : undefined
        }
      >
        <input
          id={versionId}
          className="input input--narrow mono"
          value={versionNumber}
          inputMode="numeric"
          onChange={(event) => setVersionNumber(event.target.value)}
        />
      </Field>

      <Field
        label="Which measure?"
        htmlFor={metricId}
        hint="A measure that is not the bar's own is still recorded — it is a real measurement of something else, and it does not move the gate."
      >
        <select
          id={metricId}
          className="select"
          value={metric}
          onChange={(event) => setMetric(event.target.value)}
        >
          {options.map((option) => (
            <option key={option.metric} value={option.metric}>
              {option.label}
            </option>
          ))}
        </select>
      </Field>

      <Field
        label="The number"
        htmlFor={valueId}
        error={
          value.trim() !== "" && measured === null ? NOT_A_NUMBER : undefined
        }
      >
        <input
          id={valueId}
          className="input input--narrow mono"
          value={value}
          inputMode="decimal"
          onChange={(event) => setValue(event.target.value)}
        />
      </Field>

      <Field
        label="The run it came from"
        htmlFor={runId}
        hint="Required. A result with no run behind it is a claim, not a measurement."
        error={record.isError ? errorMessage(record.error) : undefined}
      >
        <input
          id={runId}
          className="input input--mono"
          value={runUrl}
          onChange={(event) => setRunUrl(event.target.value)}
          placeholder="https://…/runs/9ab3"
        />
      </Field>

      <Field label="Notes" htmlFor={notesId} hint="Optional.">
        <input
          id={notesId}
          className="input"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Seldon-small, 9 Aug"
        />
      </Field>
    </Dialog>
  );
}

interface BarSectionProps {
  dataset: Dataset;
  config: ConsoleConfig | undefined;
}

/**
 * The bar, its history, and every attempt against it.
 *
 * Every attempt is listed, including the ones that did not clear it: a list
 * that hid the misses would be a claim about rigour the list cannot support.
 *
 * Args:
 *     dataset: The dataset.
 *     config: The published configuration, for metric labels.
 *
 * Returns:
 *     The deal section.
 */
export function BarSection({ dataset, config }: BarSectionProps): JSX.Element {
  const targets = useTargets(dataset.id);
  const evaluations = useEvaluations(dataset.id);
  const [settingBar, setSettingBar] = useState(false);
  const [recording, setRecording] = useState(false);
  const readOnly = dataset.deleted_at !== null;

  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">The bar, and every attempt at it</h2>
        {readOnly ? null : (
          <span className="page-head__actions">
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => setSettingBar(true)}
            >
              Record a bar
            </button>
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => setRecording(true)}
            >
              Record a result
            </button>
          </span>
        )}
      </div>

      {evaluations.isPending ? (
        <LoadingBlock label="Reading the attempts…" />
      ) : evaluations.isError ? (
        <ErrorBlock
          error={evaluations.error}
          onRetry={() => void evaluations.refetch()}
        />
      ) : evaluations.data.items.length === 0 ? (
        <EmptyBlock label="Nothing has been recorded against this dataset yet." />
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th scope="col">Result</th>
              <th scope="col">Version</th>
              <th scope="col">Against the bar</th>
              <th scope="col">Who, when</th>
              <th scope="col">Run</th>
            </tr>
          </thead>
          <tbody>
            {evaluations.data.items.map((evaluation) => (
              <tr key={evaluation.id}>
                <td>
                  <span className="admin-table__name mono">
                    {formatScore(evaluation.value)}
                  </span>
                  <span className="admin-table__sub">
                    {metricLabel(config, dataset.task_type, evaluation.metric)} ·
                    reported
                  </span>
                </td>
                <td className="mono">{evaluation.version_number}</td>
                <td>
                  {evaluation.passed === null ? (
                    <span className="admin-table__muted">
                      {evaluation.passed_reason ?? UNKNOWN}
                    </span>
                  ) : evaluation.passed ? (
                    <span className="badge" data-tone="ok">
                      cleared {formatScore(evaluation.threshold_value)}
                    </span>
                  ) : (
                    <span className="badge" data-tone="warning">
                      not yet past {formatScore(evaluation.threshold_value)}
                    </span>
                  )}
                </td>
                <td>
                  <span className="admin-table__name">
                    {evaluation.recorded_by}
                  </span>
                  <span className="admin-table__sub">
                    {formatDateTime(evaluation.recorded_at)}
                  </span>
                  {evaluation.notes ? (
                    <span className="admin-table__sub">{evaluation.notes}</span>
                  ) : null}
                </td>
                <td>
                  <a
                    href={evaluation.run_url}
                    rel="noreferrer noopener"
                    target="_blank"
                  >
                    the run
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {targets.isSuccess && targets.data.items.length > 1 ? (
        <details className="form__more">
          <summary>The bar has moved {targets.data.items.length - 1} time(s)</summary>
          <ol className="ledger">
            {targets.data.items.map((target) => (
              <li
                className="ledger__row"
                data-current={target.superseded_at === null}
                key={target.id}
              >
                <span className="ledger__permissions">
                  Beat {formatScore(target.threshold_value)}{" "}
                  {metricLabel(config, dataset.task_type, target.metric)}
                  {target.baseline_value === null
                    ? ` · no baseline: ${target.baseline_absent_reason ?? UNKNOWN}`
                    : ` · theirs ${formatScore(target.baseline_value)}`}
                </span>
                <span className="ledger__evidence">{target.evidence}</span>
                <span className="ledger__meta">
                  {target.set_by} · {formatDateTime(target.at)}
                </span>
              </li>
            ))}
          </ol>
        </details>
      ) : null}

      {settingBar ? (
        <RecordBarDialog
          dataset={dataset}
          config={config}
          onClose={() => setSettingBar(false)}
        />
      ) : null}

      {recording ? (
        <RecordEvaluationDialog
          dataset={dataset}
          config={config}
          onClose={() => setRecording(false)}
        />
      ) : null}
    </section>
  );
}
